import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { fetchProject, fetchCollectoren, fetchMateriaallijst, listProjectPlans } from '@/api/tasks';
import type { ProjectDetail, Collector, MateriaalLine, StockPicking, StockMove, PhotoItem } from '@/types/odoo';
import { WerfTaakTab } from '@/components/warehouse/WerfTaakTab';
import { WerfCollectorenTab } from '@/components/warehouse/WerfCollectorenTab';
import { WerfMateriaalTab } from '@/components/warehouse/WerfMateriaalTab';
import { OfferteTab } from '@/components/project/OfferteTab';

type WerfTab = 'taak' | 'collectoren' | 'materiaal' | 'offerte';

const TABS: Array<{ id: WerfTab; label: string }> = [
  { id: 'taak', label: 'Taak' },
  { id: 'collectoren', label: 'Collectoren' },
  { id: 'materiaal', label: 'Materiaal' },
  { id: 'offerte', label: 'Offerte' },
];

interface RouteState {
  projectName?: string;
  werfAddress?: string;
  saleOrderName?: string;
}

export function WarehouseWerfDetailView() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pid = Number(projectId);
  const faseParam = searchParams.get('fase');
  const faseTaskId = faseParam ? Number(faseParam) : null;
  const routeState = (location.state as RouteState) || {};

  const [activeTab, setActiveTab] = useState<WerfTab>('taak');

  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [collectors, setCollectors] = useState<Collector[]>([]);
  const [plans, setPlans] = useState<PhotoItem[]>([]);
  const [materiaal, setMateriaal] = useState<{ lines: MateriaalLine[]; pickings: StockPicking[]; moves: StockMove[] } | null>(null);

  const [detailLoading, setDetailLoading] = useState(true);
  const [collectorsLoading, setCollectorsLoading] = useState(true);
  const [materiaalLoading, setMateriaalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pid) return;
    let active = true;

    setDetailLoading(true);
    setError(null);
    fetchProject(pid)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Project laden mislukt');
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    setCollectorsLoading(true);
    fetchCollectoren(pid)
      .then((d) => {
        if (active) setCollectors(d.collectors);
      })
      .catch(() => {
        // collectoren tab shows its own empty state
      })
      .finally(() => {
        if (active) setCollectorsLoading(false);
      });

    setMateriaalLoading(true);
    fetchMateriaallijst(pid)
      .then((d) => {
        if (active) setMateriaal({ lines: d.lines, pickings: d.pickings, moves: d.moves });
      })
      .catch(() => {
        if (active) setMateriaal({ lines: [], pickings: [], moves: [] });
      })
      .finally(() => {
        if (active) setMateriaalLoading(false);
      });

    listProjectPlans(pid)
      .then((d) => {
        if (active) setPlans(d.plans);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [pid]);

  // Canonical project name (detail wins, then the card's name). Used for S3 photo
  // keys — never the `Project ${id}` placeholder, which would break key derivation.
  const resolvedProjectName = detail?.projectName || routeState.projectName || '';
  const projectName = resolvedProjectName || `Project ${projectId}`;
  const subtitle = [
    routeState.werfAddress || detail?.werfLocation,
    detail?.saleOrder?.name || routeState.saleOrderName,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="mx-auto max-w-lg lg:max-w-3xl">
      {/* Navy header — back + title + tabs */}
      <header className="bg-td-navy text-white px-4 pt-5 pb-3 shadow-md lg:rounded-b-2xl">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/werven')}
            className="-ml-1 p-1 rounded-lg hover:bg-white/10"
            aria-label="Terug naar werven"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div className="min-w-0">
            <h1 className="text-base font-extrabold leading-tight truncate">{projectName}</h1>
            {subtitle && <p className="text-[11px] text-white/60 truncate">{subtitle}</p>}
          </div>
        </div>

        <div className="mt-3 flex gap-1.5 overflow-x-auto -mx-1 px-1" role="tablist" aria-label="Werf onderdelen">
          {TABS.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 px-3 py-1.5 text-xs rounded-full transition-colors ${
                  selected ? 'bg-white text-td-navy font-bold' : 'bg-white/10 text-white/80 font-semibold'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="px-3 py-3">
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-3" role="alert">
            <p className="font-semibold">Fout bij laden</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {activeTab === 'taak' &&
          (detail ? (
            <WerfTaakTab
              detail={detail}
              collectors={collectors}
              plans={plans}
              faseTaskId={faseTaskId}
              werfAddress={routeState.werfAddress ?? null}
            />
          ) : (
            detailLoading && (
              <div className="py-12 text-center" role="status">
                <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin">
                  <span className="sr-only">Laden...</span>
                </div>
              </div>
            )
          ))}

        {activeTab === 'collectoren' && (
          <WerfCollectorenTab collectors={collectors} projectName={resolvedProjectName} loading={collectorsLoading} />
        )}

        {activeTab === 'materiaal' && (
          <WerfMateriaalTab
            pickings={materiaal?.pickings ?? []}
            lines={materiaal?.lines ?? []}
            moves={materiaal?.moves ?? []}
            loading={materiaalLoading}
          />
        )}

        {activeTab === 'offerte' &&
          (detail ? (
            <OfferteTab
              saleOrders={detail.saleOrders}
              saleOrder={detail.saleOrder}
              lines={detail.saleOrderLines}
              partnerWarning={detail.partnerWarning}
            />
          ) : (
            detailLoading && (
              <div className="py-12 text-center" role="status">
                <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin">
                  <span className="sr-only">Laden...</span>
                </div>
              </div>
            )
          ))}
      </div>
    </div>
  );
}
